import { PaymentMethodProps } from './PaymentMethod';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../store/types';
import useAuth from '../../../hooks/global/useAuth';
import SavePaymentInfoConsent from './SavePaymentInfoConsent';
import AttachablePaymentMethod from './AttachablePaymentMethod';
import { PaymentGateway, PaymentMethod } from '../../../services/exports/Constants';
import classNames from 'classnames';
import SpreedlyCardForm from '../spreedly/SpreedlyCardForm';

/**
 * The card row the shop draws itself: the card on file on the Stripe Elements checkout (the Element
 * takes a new card), and the card under Spreedly, whose form opens below the row.
 */
export default function CardPaymentMethod(props: PaymentMethodProps) {
  const { isLastItem, className } = props;
  const { selectedPaymentMethod, paymentOptions, findSavedPaymentMethod, getGatewayForMethod } = usePaymentFlow();

  const isSpreedly = getGatewayForMethod(PaymentMethod.Card) === PaymentGateway.Spreedly;
  const savedCard = useMemo(() => findSavedPaymentMethod(PaymentMethod.Card), [paymentOptions]);

  // The Spreedly form sits BELOW the row header on its own grey holder; the blue stays on the header
  // alone. That holder only exists while Card is the selected method and no card is on file (a saved
  // card needs no form). The row hands its rounded bottom corners to the holder under exactly that
  // condition and no other: with Cash or PayPal selected, or a card on file, there is nothing below
  // the row and it closes the list itself.
  const showsSpreedlyHolder = isSpreedly && selectedPaymentMethod === PaymentMethod.Card && !savedCard;

  return (
    <AttachablePaymentMethod
      {...props}
      className={classNames(className, { '!rounded-b-none': showsSpreedlyHolder && isLastItem })}
    >
      {showsSpreedlyHolder && <SpreedlyCardHolder isLastItem={isLastItem} />}
    </AttachablePaymentMethod>
  );
}

function SpreedlyCardHolder({ isLastItem }: { isLastItem: boolean }) {
  const { isGuest } = useAuth();
  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const { spreedlyCardFormRef, isGiftCardCheckout } = usePaymentFlow();

  return (
    // `isLastItem` only decides whether the holder closes the list with rounded corners. The holder
    // pads its content itself — the card form and the consent below it carry no gutter of their own.
    // The form is wired to the pay button through the context ref, so the summary column keeps only
    // the button, consistent with every other gateway.
    <div className={classNames(holder, { 'rounded-b-2xl': isLastItem })} data-test="spreedly-card-holder">
      <SpreedlyCardForm ref={spreedlyCardFormRef} />
      {/* Not on the gift-card checkout: that request never carries the consent, so a
          checkbox there would promise a saved card that nothing writes. */}
      {!isGuest && !isGiftCardCheckout && company?.saved_cards_enabled && (
        // The theme zeroes the MUI checkbox's padding, so the only thing between the drawn box
        // and the component's edge is the icon's own 3px inner inset. Pulled back by exactly that
        // (as dev does) so the box lines up with the field borders above it.
        <SavePaymentInfoConsent className="mt-sm -ml-[3px]" />
      )}
    </div>
  );
}

// The row above already draws a bottom border, so the holder skips its top one: one hairline
// between header and form. Dark mode matches the checked row's border.
const holder = 'p-sm border border-t-0 bg-[#F5F5F5] dark:bg-black dark:border-brand-border';
